import { useState } from 'react';
import { useRouter } from 'next/router';
import {
  DATE_FORMATS,
  formatUtcDateTimeToTimeZone,
  BASE_PATHS,
  PATHS,
} from '@belong/common';
import { IconClockOutline, IconSelfTourLockbox } from '@belong/icons';
import {
  Toast,
  ToastProps,
  Text,
  Image,
  Button,
  Accordion,
  DashedLine,
  Link,
  Spinner,
} from '@belong/ui';
import {
  UnitListingDetailsModel,
  TourAttendeeDetailModel,
} from 'src/api/models';
import { HOUSE_PAGE_STRINGS } from 'src/features/house-page/house-page.strings';
import { isSelfTourOffered } from 'src/features/house-page/utils';
import { usePreQualificationGate } from 'src/features/resident-pre-qualification/use-pre-qualification-gate';
import { getPreQualificationGateMessage } from 'src/features/resident-pre-qualification/utils';
import { isTourRequestActive } from 'src/features/residents/utils';
import { ApiError } from 'src/utils/build-api-client';
import { useSWRConfig } from 'swr';
import {
  useBookTour,
  useConfirmBookTour,
  useGetResidentTourRequests,
  useOpenHomeTours,
  useRescheduleBookTour,
} from '../../../queries';
import { SelfTourCard } from '../self-tour-card';
import { TourModalStep } from '../tour-modal';

type TourSelectionProps = {
  home: UnitListingDetailsModel;
  // Optional: undefined until `TourModal`'s booked-tours query resolves, which
  // every read below already guards with `?.`.
  bookedTour?: TourAttendeeDetailModel;
  setStep: (step: TourModalStep) => void;
};

const MAX_OCCUPIED_TOUR_ATTENDEES = 10;

export function TourSelection({
  home,
  bookedTour,
  setStep,
}: TourSelectionProps) {
  const { mutate } = useSWRConfig();
  const router = useRouter();
  // `!` here and on `homeTours.items` below: this step only renders inside the
  // house page's tour modal, and both were already read unconditionally.
  const homeId = home.basicInfo!.unitId!;
  const { homeTours, isHomeToursLoading } = useOpenHomeTours({ homeId });
  const {
    guard: guardPreQualification,
    guardAfterServerError,
    content: preQualificationContent,
    isGateLoading,
  } = usePreQualificationGate({ homeId });
  const { data: tourRequests } = useGetResidentTourRequests();
  const hasActiveTourRequest = !!tourRequests?.some(
    (tourRequest) =>
      tourRequest.homeUniqueId === homeId && isTourRequestActive(tourRequest),
  );

  const refetchBookedTourConfig = {
    onSuccess: () => mutate(`/homes/${homeId}/tours/booked`),
  };

  const { mutateBookTour, isBookTourMutating } = useBookTour(
    { homeId },
    refetchBookedTourConfig,
  );
  const { mutateReschedule, isReschedulingBookTourMutating } =
    useRescheduleBookTour(refetchBookedTourConfig);
  const { mutateConfirm, isConfirmingBookTourMutating } = useConfirmBookTour(
    refetchBookedTourConfig,
  );

  const [toastConfig, setToastConfig] = useState<Partial<ToastProps> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const { isOccupied } = home?.listingInfo || {};
  const { timeZone } = home?.basicInfo || {};
  const showSelfTour =
    isSelfTourOffered(home?.listingInfo) && !!router.query.slug;
  const selfTourHref = `/homes/${router.query.slug}/${homeId}/self-tour`;

  const { attendeeStatus, uniqueId: attendeeUniqueId } =
    bookedTour?.tourAttendee || {};
  const isReservedSelfTour = bookedTour?.isReserved === true;
  const selfTourRescheduleHref = `${PATHS.RESIDENT_SELF_TOUR_VISIT}/${bookedTour?.tour?.uniqueId}?step=reschedule`;
  // Self Tour reservations are managed in their own flow, not by group-tour actions.
  const activeTour =
    bookedTour?.tour?.status === 'Scheduled' &&
    bookedTour.tour.type !== 'SelfTour'
      ? bookedTour.tour
      : undefined;
  const attendeeId = activeTour ? attendeeUniqueId : undefined;
  const invitedTour = attendeeStatus === 'Invited' ? activeTour : undefined;
  const invitedTourId = invitedTour?.uniqueId;
  const bookedTourId =
    attendeeStatus === 'Booked' ? activeTour?.uniqueId : undefined;

  const isMutating =
    isBookTourMutating ||
    isReschedulingBookTourMutating ||
    isConfirmingBookTourMutating;

  async function handleBook(tourId: string) {
    setError(null);

    try {
      if (tourId === invitedTourId) {
        await mutateConfirm({ tourId });
      } else if (attendeeId) {
        await mutateReschedule({
          newTourUniqueId: tourId,
          tourAttendeeUniqueId: attendeeId,
        });
      } else {
        await mutateBookTour({ tourId });
      }
    } catch (error) {
      if (guardAfterServerError('BookTour', error, () => handleBook(tourId))) {
        return;
      }

      const errorMessage =
        error instanceof ApiError
          ? (getPreQualificationGateMessage(error) ??
            HOUSE_PAGE_STRINGS['tour.errorMessages'][
              error.code as keyof (typeof HOUSE_PAGE_STRINGS)['tour.errorMessages']
            ])
          : undefined;

      setError(errorMessage ?? HOUSE_PAGE_STRINGS['tour.bookingError']);

      return;
    }

    setStep(TourModalStep.TourScheduled);
  }

  const openHomeSlots = (homeTours?.items ?? [])
    .filter(
      (slot): slot is typeof slot & { tourUniqueId: string } =>
        !!slot.tourUniqueId,
    )
    .map((slot) => ({
      tourId: slot.tourUniqueId,
      startDate: slot.startDate,
      endDate: slot.endDate,
      isOccupied: slot.isOccupied,
      isFull:
        slot.isOccupied && slot.bookedAttendees >= MAX_OCCUPIED_TOUR_ATTENDEES,
    }));

  const isInvitedTourListed = openHomeSlots.some(
    (slot) => slot.tourId === invitedTourId,
  );

  const tourRows = [
    ...openHomeSlots,
    ...(invitedTourId && !isInvitedTourListed
      ? [
          {
            tourId: invitedTourId,
            startDate: invitedTour!.startOn,
            endDate: invitedTour!.endOn,
            isOccupied: false,
            isFull: false,
          },
        ]
      : []),
  ].sort(
    (slot1, slot2) =>
      new Date(slot1.startDate).getTime() - new Date(slot2.startDate).getTime(),
  );

  if (preQualificationContent) {
    return preQualificationContent;
  }

  if (isHomeToursLoading) {
    return <Spinner />;
  }

  const hasGroupTours = tourRows.length > 0;

  const isInvitedTourFull = tourRows.some(
    (slot) => slot.tourId === invitedTourId && slot.isFull,
  );

  if (
    isInvitedTourFull &&
    tourRows.some((slot) => !slot.isFull) &&
    !toastConfig
  ) {
    setToastConfig({
      children: HOUSE_PAGE_STRINGS['tour.tourIsFull'],
      variant: 'danger',
      autocloseDuration: 10000,
      className: 'max-w-default-modal',
    });
  }

  const occupiedOpenHomes = homeTours?.items!.filter((item) => item.isOccupied);
  const allOpenHomeOccupiedAndFull = occupiedOpenHomes!.every(
    (item) => item.bookedAttendees >= MAX_OCCUPIED_TOUR_ATTENDEES,
  );

  if (
    occupiedOpenHomes!.length > 0 &&
    allOpenHomeOccupiedAndFull &&
    !isReservedSelfTour
  ) {
    return (
      <div className="px-sm w-full items-center">
        <div className="mb-xl px-sm m-auto text-center">
          <Text variant="h2" fontWeight="semibold">
            {HOUSE_PAGE_STRINGS['tour.cantConfirm']}
          </Text>
          <Text className="mt-xs">{HOUSE_PAGE_STRINGS['tour.tourIsFull']}</Text>
        </div>
        <div className="flex items-center justify-center">
          <Link
            buttonType="primary"
            variant="solid"
            href={BASE_PATHS.HOME}
            size="default"
            className="mb-2xl"
          >
            {HOUSE_PAGE_STRINGS['tour.viewListings']}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isMutating && <Spinner />}
      <Toast
        isVisible={!!toastConfig}
        onClose={() => setToastConfig(null)}
        {...toastConfig}
      />
      <div className="w-full">
        <div className="mb-xl px-sm m-auto text-center">
          <Text variant="h2" fontWeight="semibold">
            {HOUSE_PAGE_STRINGS['tour.comeSeeThisHome']}
          </Text>
          <Text className="mt-xs text-dark-gray">
            {HOUSE_PAGE_STRINGS['tour.chooseHowToSee']}
          </Text>
          {error && (
            <Text variant="p1" fontWeight="semibold" className="mt-xs text-red">
              {error}
            </Text>
          )}
        </div>
        <div className="px-sm pb-3xl">
          {(showSelfTour || isReservedSelfTour) && (
            <>
              {isReservedSelfTour ? (
                <div className="border-gray p-sm gap-sm flex flex-col rounded border border-solid">
                  <div className="gap-sm flex items-center">
                    <div className="flex shrink-0">
                      <IconSelfTourLockbox width={60} className="text-navy" />
                    </div>
                    <div className="gap-x-xs flex flex-1 flex-wrap items-center">
                      <Text fontWeight="semibold">
                        {HOUSE_PAGE_STRINGS['tour.selfTour']}
                      </Text>
                      <span className="text-navy hidden md:inline">|</span>
                      <Text
                        fontWeight="semibold"
                        className="text-p1 md:text-body order-last w-full whitespace-nowrap md:order-none md:w-auto"
                      >
                        {formatUtcDateTimeToTimeZone({
                          dateTime: bookedTour.tour!.startOn,
                          format: DATE_FORMATS.LONG_DATE,
                          timeZone,
                        })}
                        {' · '}
                        {formatUtcDateTimeToTimeZone({
                          dateTime: bookedTour.tour!.startOn,
                          format: DATE_FORMATS.TIME_TIMEZONE,
                          timeZone,
                        })}
                      </Text>
                      <Text
                        as="span"
                        variant="p1"
                        fontWeight="semibold"
                        className="px-xs rounded-small bg-navy ml-auto shrink-0 text-white"
                      >
                        Reserved
                      </Text>
                    </div>
                  </div>
                  <div className="gap-xs border-gray py-xs text-navy flex items-center border-x-0 border-y border-dashed">
                    <IconClockOutline width={20} className="shrink-0" />
                    <Text variant="p1">
                      Verify your ID to confirm
                      {bookedTour.confirmBy && (
                        <span className="block whitespace-nowrap md:inline">
                          {' by '}
                          {formatUtcDateTimeToTimeZone({
                            dateTime: bookedTour.confirmBy,
                            format: DATE_FORMATS.MONTH_DAY_SHORT,
                            timeZone,
                          })}
                          {', '}
                          {formatUtcDateTimeToTimeZone({
                            dateTime: bookedTour.confirmBy,
                            format: DATE_FORMATS.TIME_TIMEZONE,
                            timeZone,
                          })}
                        </span>
                      )}
                    </Text>
                  </div>
                  <div className="gap-xl md:gap-xs flex flex-col-reverse items-center md:flex-row md:justify-between">
                    <div className="gap-xl flex">
                      <Link variant="text" href={selfTourRescheduleHref}>
                        {HOUSE_PAGE_STRINGS['tour.reschedule']}
                      </Link>
                      <Button
                        variant="text"
                        buttonType="danger"
                        onClick={() => setStep(TourModalStep.TourCancel)}
                      >
                        {HOUSE_PAGE_STRINGS['tour.cancelTour']}
                      </Button>
                    </div>
                    <Link
                      buttonType="primary"
                      variant="solid"
                      size={{ _: 'fluid', md: 'default' }}
                      href={selfTourHref}
                    >
                      Finish Verifying
                    </Link>
                  </div>
                </div>
              ) : (
                <SelfTourCard
                  homeId={homeId}
                  href={selfTourHref}
                  timeZone={timeZone}
                />
              )}
              <div className="gap-sm my-xl flex items-center">
                <DashedLine />
                <Text variant="p1" className="text-gray shrink-0">
                  {HOUSE_PAGE_STRINGS['tour.or']}
                </Text>
                <DashedLine />
              </div>
            </>
          )}
          {hasGroupTours ? (
            <div>
              <Text
                fontWeight="semibold"
                className="mb-sm text-center"
                variant="h3"
              >
                {HOUSE_PAGE_STRINGS['tour.attendGroupTour']}
              </Text>
              {isReservedSelfTour && (
                <Text variant="p1" className="mb-sm text-center text-dark-gray">
                  Cancel your Self Tour reservation to book a group tour.
                </Text>
              )}
              <div className="gap-xs flex flex-col">
                {tourRows.map((slot) => (
                  <div
                    key={slot.tourId}
                    className="border-gray p-sm gap-sm md:flex-row md:items-center flex flex-col rounded border border-solid"
                  >
                    <div className="gap-sm md:mr-auto flex items-center">
                      <div className="flex shrink-0">
                        <div className="size-2xl">
                          <Image
                            src="https://belong-consumer-assets.s3.us-west-1.amazonaws.com/common-assets/ic_group_tour.svg"
                            alt=""
                          />
                        </div>
                      </div>
                      <div className="gap-2xs flex flex-col items-start">
                        <Text fontWeight="semibold">
                          {`${formatUtcDateTimeToTimeZone({ dateTime: slot.startDate, format: DATE_FORMATS.TIME, timeZone })} - ${formatUtcDateTimeToTimeZone({ dateTime: slot.endDate, format: DATE_FORMATS.TIME_TIMEZONE, timeZone })}`}
                        </Text>
                        <Text
                          variant="p1"
                          className="text-dark-gray md:order-none order-first"
                        >
                          {formatUtcDateTimeToTimeZone({
                            dateTime: slot.startDate,
                            format: DATE_FORMATS.LONG_DATE,
                            timeZone,
                          })}
                        </Text>
                        {slot.isOccupied && (
                          <span className="px-xs rounded-small bg-navy text-p1 text-white">
                            {HOUSE_PAGE_STRINGS['tour.occupied']}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="gap-sm flex w-full flex-col items-center md:w-auto">
                      {slot.tourId !== bookedTourId && (
                        <Button
                          size={{ _: 'fluid', md: 'default' }}
                          disabled={
                            isReservedSelfTour ||
                            slot.isFull ||
                            isMutating ||
                            isGateLoading
                          }
                          onClick={() =>
                            guardPreQualification('BookTour', () =>
                              handleBook(slot.tourId),
                            )
                          }
                        >
                          {HOUSE_PAGE_STRINGS['tour.book']}
                        </Button>
                      )}
                      {slot.tourId === invitedTourId && (
                        <Button
                          variant="text"
                          onClick={() => setStep(TourModalStep.TourCancel)}
                        >
                          {HOUSE_PAGE_STRINGS['tour.cantMakeIt']}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Text className="text-center">
              {HOUSE_PAGE_STRINGS['tour.noGroupTours']}
            </Text>
          )}

          {isOccupied && (
            <div className="mb-xl mt-2xl">
              <div className="border-green bg-green-light-bg px-sm rounded border border-solid">
                <Accordion
                  items={[
                    {
                      title:
                        HOUSE_PAGE_STRINGS['occupied_tour.bullet_list.title'],
                      content: (
                        <div>
                          <div className="mb-2xs flex">
                            <div className="px-xs">&bull;</div>
                            <Text>
                              {
                                HOUSE_PAGE_STRINGS[
                                  'occupied_tour.bullet_list.first'
                                ]
                              }
                            </Text>
                          </div>
                          <div className="mb-2xs flex">
                            <div className="px-xs">&bull;</div>
                            <Text>
                              {
                                HOUSE_PAGE_STRINGS[
                                  'occupied_tour.bullet_list.second'
                                ]
                              }
                            </Text>
                          </div>
                          <div className="mb-2xs flex">
                            <div className="px-xs">&bull;</div>
                            <Text>
                              {
                                HOUSE_PAGE_STRINGS[
                                  'occupied_tour.bullet_list.third'
                                ]
                              }
                            </Text>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                  index={isAccordionOpen ? 0 : -1}
                  onChange={() => setIsAccordionOpen((prev) => !prev)}
                  withDivider={false}
                />
              </div>
            </div>
          )}

          {!showSelfTour && (
            <div className="mt-2xl flex flex-col items-center">
              <Text>{HOUSE_PAGE_STRINGS['tour.doesntWorkForYou']}</Text>
              <Button
                variant="text"
                disabled={isGateLoading}
                onClick={() => {
                  const goToSendAvailability = () =>
                    setStep(TourModalStep.TourSendAvailability);

                  if (hasActiveTourRequest) {
                    goToSendAvailability();

                    return;
                  }

                  guardPreQualification('RequestTour', goToSendAvailability);
                }}
              >
                {HOUSE_PAGE_STRINGS['tour.sendAvailability']}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
